import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  createStaff,
  deactivateStaff,
  fetchPermissionCatalog,
  fetchPermissionTemplate,
  fetchStaff,
  updateStaff,
  updateStaffPermissions,
  type PermissionCatalogEntry,
} from "@/lib/staffApi";
import { hasPermission, STAFF_ROLE_LABELS, type StaffMember, type StaffRole } from "@/lib/types";
import { cn } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, UserX, UserCog, Info, KeyRound } from "lucide-react";
import { useApp } from "@/store/useApp";
import { PermissionsEditor } from "@/components/admin/PermissionsEditor";

const ROLE_OPTIONS: { value: StaffRole; label: string }[] = [
  { value: "ADMIN", label: "Administrador" },
  { value: "ADMINISTRATIVO", label: "Administrativo" },
  { value: "VENDEDOR", label: "Vendedor" },
  { value: "PLANEJADOR", label: "Planejador" },
  { value: "COMPRADOR", label: "Comprador" },
  { value: "CONFERENTE", label: "Conferente" },
  { value: "ENTREGADOR", label: "Entregador" },
  { value: "PRODUCAO", label: "Produção" },
  { value: "EXPEDICAO", label: "Expedição" },
  { value: "SEPARACAO", label: "Separação IN NATURA" },
];

const ROLE_COLORS: Record<StaffRole, string> = {
  ADMIN: "bg-primary/10 text-primary border-primary/30",
  ADMINISTRATIVO: "bg-indigo-500/10 text-indigo-700 border-indigo-500/30",
  VENDEDOR: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  PLANEJADOR: "bg-violet-500/10 text-violet-700 border-violet-500/30",
  COMPRADOR: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  CONFERENTE: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  ENTREGADOR: "bg-sky-500/10 text-sky-700 border-sky-500/30",
  PRODUCAO: "bg-orange-500/10 text-orange-700 border-orange-500/30",
  EXPEDICAO: "bg-teal-500/10 text-teal-700 border-teal-500/30",
  SEPARACAO: "bg-lime-500/10 text-lime-700 border-lime-500/30",
};

interface FormState {
  email: string;
  name: string;
  role: StaffRole | "";
  password: string;
  commissionPercent: string;
}

const EMPTY_FORM: FormState = { email: "", name: "", role: "", password: "", commissionPercent: "" };

function isFullAdminRole(role: StaffRole | ""): boolean {
  return role === "ADMIN" || role === "ADMINISTRATIVO";
}

export default function AdminFuncionarios() {
  const account = useApp((s) => s.account);
  // Gates por permissão (o backend revalida; esconder o controle é só conveniência de UI).
  const canCreate = hasPermission(account, "STAFF.FUNCIONARIO.CADASTRAR");
  const canEdit = hasPermission(account, "STAFF.FUNCIONARIO.EDITAR");
  const canDeactivate = hasPermission(account, "STAFF.FUNCIONARIO.DESATIVAR");
  const canConfigureAccess = hasPermission(account, "STAFF.ACESSO.CONFIGURAR");
  // Proteções de UI (o backend é a barreira real): ninguém altera o próprio acesso/cargo; ADMINISTRATIVO
  // nunca gerencia conta ADMIN. Conta de sessão não tem id — comparo por e-mail.
  const selfEmail = account?.email;
  const isSelf = (m: StaffMember) => !!selfEmail && m.email === selfEmail;
  const actorIsAdministrativo = account?.role === "ADMINISTRATIVO";
  const administrativoBlocks = (m: StaffMember) => actorIsAdministrativo && m.role === "ADMIN";

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [catalog, setCatalog] = useState<PermissionCatalogEntry[]>([]);

  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [editTarget, setEditTarget] = useState<StaffMember | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [createPerms, setCreatePerms] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<StaffMember | null>(null);

  const [accessTarget, setAccessTarget] = useState<StaffMember | null>(null);
  const [accessPerms, setAccessPerms] = useState<Set<string>>(new Set());
  const [savingAccess, setSavingAccess] = useState(false);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const [list, cat] = await Promise.all([fetchStaff(), fetchPermissionCatalog().catch(() => [])]);
      setStaff(list);
      setCatalog(cat);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setCreatePerms(new Set());
    setModalMode("create");
  };

  const openEdit = (member: StaffMember) => {
    setEditTarget(member);
    setForm({
      email: member.email,
      name: member.name,
      role: member.role,
      password: "",
      commissionPercent: member.commissionPercent != null ? String(member.commissionPercent) : "",
    });
    setModalMode("edit");
  };

  const closeModal = () => { setModalMode(null); setEditTarget(null); setForm(EMPTY_FORM); setCreatePerms(new Set()); };

  const applyTemplate = async () => {
    if (!form.role) { toast.error("Selecione um cargo primeiro"); return; }
    try {
      const codes = await fetchPermissionTemplate(form.role as StaffRole);
      setCreatePerms(new Set(codes));
      toast.success("Modelo do cargo aplicado — ajuste se necessário");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível carregar o modelo"));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.role) { toast.error("Selecione um cargo"); return; }
    if (modalMode === "create" && !form.password) { toast.error("Senha obrigatória"); return; }

    let commissionPercent: number | null | undefined;
    if (form.role === "VENDEDOR") {
      const trimmed = form.commissionPercent.trim();
      if (trimmed === "") {
        commissionPercent = null;
      } else {
        const parsed = Number(trimmed);
        if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
          toast.error("% de comissão deve estar entre 0 e 100");
          return;
        }
        commissionPercent = parsed;
      }
    }

    setSubmitting(true);
    try {
      if (modalMode === "create") {
        const created = await createStaff({
          email: form.email,
          name: form.name,
          role: form.role as StaffRole,
          password: form.password,
          commissionPercent,
          permissions: Array.from(createPerms),
        });
        setStaff((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`${created.name} cadastrado com sucesso`);
      } else if (editTarget) {
        const updated = await updateStaff(editTarget.id, {
          email: form.email,
          name: form.name,
          role: form.role as StaffRole,
          password: form.password || undefined,
          commissionPercent,
        });
        setStaff((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
        toast.success("Funcionário atualizado");
      }
      closeModal();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível salvar"));
    } finally {
      setSubmitting(false);
    }
  };

  const openAccess = (member: StaffMember) => {
    setAccessTarget(member);
    setAccessPerms(new Set(member.permissions ?? []));
  };

  const saveAccess = async () => {
    if (!accessTarget) return;
    setSavingAccess(true);
    try {
      const updated = await updateStaffPermissions(accessTarget.id, Array.from(accessPerms));
      setStaff((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      toast.success("Acessos atualizados");
      setAccessTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível salvar os acessos"));
    } finally {
      setSavingAccess(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await deactivateStaff(deactivateTarget.id);
      setStaff((prev) => prev.filter((m) => m.id !== deactivateTarget.id));
      toast.success(`${deactivateTarget.name} desativado`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setDeactivateTarget(null);
    }
  };

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-1 text-xs font-medium">
                <UserCog className="h-3 w-3" /> Gestão de funcionários
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight">Funcionários</h1>
              <p className="mt-1 text-sm text-primary-foreground/80">
                Cadastre funcionários e configure os acessos por módulo, submódulo e ação.
              </p>
            </div>
            {canCreate && (
              <Button variant="secondary" onClick={openCreate} className="shrink-0">
                <Plus className="h-4 w-4" /> Novo funcionário
              </Button>
            )}
          </div>
        </div>
      </section>

      <div className="container py-6">
        {!canCreate && !canEdit && (
          <div className="surface-card mb-4 flex items-start gap-2 border-info/30 bg-info/5 p-3 text-sm">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
            <p className="text-muted-foreground">
              Seu acesso a esta tela é somente leitura.
            </p>
          </div>
        )}
        {loadState === "loading" && (
          <div className="surface-card p-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando...
          </div>
        )}
        {loadState === "error" && (
          <div className="surface-card p-12 text-center text-sm text-destructive">
            Não foi possível carregar os funcionários.
          </div>
        )}
        {loadState === "ready" && (
          <>
            {staff.length === 0 ? (
              <div className="surface-card p-12 text-center text-sm text-muted-foreground">
                Nenhum funcionário cadastrado ainda.{" "}
                {canCreate && (
                  <button onClick={openCreate} className="text-primary underline underline-offset-2">
                    Cadastrar o primeiro
                  </button>
                )}
              </div>
            ) : (
              <div className="surface-card divide-y divide-border overflow-hidden">
                {staff.map((member) => (
                  <div key={member.id} className="flex items-center gap-4 px-5 py-3.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted font-display text-sm font-bold uppercase">
                      {member.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display text-sm font-bold">{member.name}</span>
                        <span
                          className={cn(
                            "rounded-md border px-2 py-0.5 text-[11px] font-semibold",
                            ROLE_COLORS[member.role],
                          )}
                        >
                          {STAFF_ROLE_LABELS[member.role]}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {(member.permissions?.length ?? 0)} acessos
                        </span>
                        {member.status === "BLOQUEADO" && (
                          <span className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold text-destructive">
                            Inativo
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{member.email}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {canConfigureAccess && !isSelf(member) && !administrativoBlocks(member) && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Configurar acessos"
                          onClick={() => openAccess(member)}>
                          <KeyRound className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canEdit && !administrativoBlocks(member) && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(member)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canDeactivate && !isSelf(member) && !administrativoBlocks(member) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeactivateTarget(member)}
                        >
                          <UserX className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit modal */}
      <Dialog open={modalMode !== null} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{modalMode === "create" ? "Novo funcionário" : "Editar funcionário"}</DialogTitle>
            <DialogDescription>
              {modalMode === "create"
                ? "Preencha os dados e selecione os acessos (o cargo é só um modelo inicial)."
                : "Altere os dados do funcionário. Os acessos são configurados pelo botão 🔑."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nome completo" required />
            </div>
            <div className="space-y-1.5">
              <Label>E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="email@exemplo.com" required />
            </div>
            <div className="space-y-1.5">
              <Label>Cargo (modelo inicial)</Label>
              <Select value={form.role}
                disabled={modalMode === "edit" && editTarget != null && isSelf(editTarget)}
                onValueChange={(v) => setForm((f) => ({ ...f, role: v as StaffRole }))}>
                <SelectTrigger><SelectValue placeholder="Selecione um cargo" /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{modalMode === "create" ? "Senha" : "Nova senha (opcional)"}</Label>
              <Input type="password" value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={modalMode === "create" ? "Senha de acesso" : "Deixe em branco para manter"}
                required={modalMode === "create"} />
            </div>
            {form.role === "VENDEDOR" && (
              <div className="space-y-1.5">
                <Label>% de comissão padrão</Label>
                <Input type="number" min="0" max="100" step="0.01" value={form.commissionPercent}
                  onChange={(e) => setForm((f) => ({ ...f, commissionPercent: e.target.value }))} placeholder="Ex.: 5" />
              </div>
            )}

            {modalMode === "create" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Acessos</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => void applyTemplate()}
                    disabled={!form.role}>
                    Aplicar modelo do cargo
                  </Button>
                </div>
                <PermissionsEditor
                  catalog={catalog}
                  selected={createPerms}
                  onChange={setCreatePerms}
                  targetIsFullAdmin={isFullAdminRole(form.role)}
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>Cancelar</Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {modalMode === "create" ? "Cadastrar" : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Configure access dialog */}
      <Dialog open={accessTarget !== null} onOpenChange={(open) => !open && setAccessTarget(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Acessos — {accessTarget?.name}</DialogTitle>
            <DialogDescription>
              Marque os módulos, submódulos e ações. Alterações valem na próxima requisição do usuário.
            </DialogDescription>
          </DialogHeader>
          {accessTarget && (
            <PermissionsEditor
              catalog={catalog}
              selected={accessPerms}
              onChange={setAccessPerms}
              targetIsFullAdmin={isFullAdminRole(accessTarget.role)}
              disabled={savingAccess}
            />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAccessTarget(null)} disabled={savingAccess}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void saveAccess()} disabled={savingAccess}>
              {savingAccess && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar acessos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate confirmation */}
      <AlertDialog open={deactivateTarget !== null} onOpenChange={(open) => !open && setDeactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar funcionário?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deactivateTarget?.name}</strong> perderá o acesso ao sistema imediatamente.
              Esta ação pode ser revertida pelo suporte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void handleDeactivate()}
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
